const mainVisualContent = {
  title: '메인 비주얼 관리 매뉴얼',
  content: `
  <div class="page-title">[메인비주얼 관리]</div>

# [개요]
메인비주얼 관리 기능은 웹사이트의 메인 페이지에 노출되는 비주얼 콘텐츠를 효율적으로 관리하기 위한 도구입니다. 이 문서에서는 **비주얼 콘텐츠의 등록, 수정, 미리보기 등 전반적인 관리 방법**을 확인할 수 있습니다.

### 주요 기능 요약
| 기능 | 설명 | 권한 |
|------|------------------|------------|
| 비주얼 조회 | 등록된 메인비주얼 목록 확인 및 검색 | 관리자 |
| 비주얼 등록 | 신규 메인비주얼 생성 및 설정 | 관리자 |
| 비주얼 수정 | 기존 메인비주얼 정보 수정 | 관리자 |
| 가이드 확인 | 서비스 화면 필드 위치 가이드 | 관리자 |
| 반응형 미리보기 | 디바이스별 화면 미리보기 | 관리자 |
| 순서 변경 | 메인비주얼 순서 변경 | 관리자 |

# [조회]
등록된 메인비주얼의 목록을 조회하고 순서를 변경할 수 있습니다.

### 📋 화면 구성 및 기능
| 영역 | 구성 | 주요 기능 |
|------|------|-----------|
| 목록 영역 | 메인비주얼 목록 테이블 | • 언어/텍스트/사용여부 확인<br>• 미리보기 이미지 확인<br>• 노출 순서 확인 |
| 가이드 영역 | 메인비주얼 가이드 | • 메인비주얼 가이드 확인 |
| 버튼 영역 | 등록 버튼 | • 신규 메인비주얼 등록 |
| 순서 변경 영역 | 그리드 좌측 D&D 버튼 | • 그리드 좌측 핸들러 드래그 앤 드롭 시 순서 변경<br>• 순서 변경 시 즉시 반영 |

![메인비주얼 목록](/assets/images/guide/mainVisual/list.png)

# [가이드]
그리드 우측 상단 토글버튼(ⓘ)으로 서비스 화면에서 각 필드가 실제로 표시되는 위치를 확인할 수 있습니다.

### 📋 화면 구성 및 기능
| 영역 | 구성 | 주요 기능 |
|------|------|-----------|
| 가이드 영역 | 레이아웃 가이드 | • 각 필드별 위치 표시<br>• 필드 설명 확인 |

![메인비주얼 가이드](/assets/images/guide/mainVisual/guide.png)

# [등록/수정]
메인비주얼을 신규 등록하거나 기존 메인비주얼의 상세 정보를 수정할 수 있습니다.

> ⚠️ **주의사항**
> - 모바일 환경에서는 이미지가 자동 리사이징됩니다.
> - 해상도 높은 미디어 파일로 인해 등록, 수정 시 대기 시간이 발생할 수 있습니다.

> 💡 **컨텐츠 권장 사양**
> 
> [이미지]
> - 권장 크기: 1920 x 1080 px (16:9 비율)
> - 파일 형식: JPG, PNG
> - 최대 용량: 2MB
>
> [비디오]
> - 권장 크기: 1920 x 1080 px (FHD)
> - 파일 형식: MP4
> - 최대 용량: 10MB

### 📋 화면 구성 및 기능
| 영역 | 입력 항목 | 설명 |
|------|-----------|------|
| 기본 설정 | • 언어<br>• 메인 텍스트<br>• 서브 텍스트<br>• 배경 미디어 | • 노출 언어 선택<br>• 메인 텍스트 입력<br>• 서브 텍스트 입력<br>• 배경 미디어 선택 |

![메인비주얼 가이드](/assets/images/guide/mainVisual/edit_desktop.png)

# [미리보기]
등록된 콘텐츠가 각 디바이스에서 어떻게 보여질지 미리 확인할 수 있습니다.

> 💡 **Tip**
> - 토글 버튼으로 미리보기 화면을 접고 펼 수 있습니다.
> - 실제 서비스 환경과 디바이스 설정에 따라 화면이 다르게 보일 수 있습니다.
> - 미리보기는 참고용으로, 실제 서비스 환경에서 한번 더 확인하는 것을 권장합니다.

### 📋 화면 구성 및 기능
| 영역 | 구성 | 주요 기능 |
|------|------|-----------|
| 디바이스 선택 | 미리보기 환경 선택 | • 데스크탑/태블릿/모바일 선택<br>• 실시간 화면 전환 |
| 미리보기 영역 | 콘텐츠 미리보기 | • 실제 크기 확인<br>• 반응형 동작 확인<br>• 애니메이션 효과 확인 |
| 토글 버튼 | 화면 접기/펼치기 | • 미리보기 영역 토글<br>• 편집 화면 전환 |

<div class="image-row">
  <div class="image-col">
    <img src="/assets/images/guide/mainVisual/edit_desktop.png" alt="데스크탑 미리보기" />
  </div>
  <div class="image-col">
    <img src="/assets/images/guide/mainVisual/edit_mobile.png" alt="모바일 미리보기" />
  </div>
</div>

*데스크탑 화면(좌)과 모바일 화면(우) 미리보기*
    `,
};
export default mainVisualContent;
